import React, { useContext, useState, useRef, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { useSettings } from '../context/SettingsContext';
import Logo from './Logo';
import {
  FaShoppingCart,
  FaSignOutAlt,
  FaBars,
  FaTimes,
  FaSearch,
  FaUserCircle,
  FaBoxOpen,
  FaTachometerAlt,
} from 'react-icons/fa';
import './Navbar.css';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const { getCartCount } = useContext(CartContext);
  const { settings } = useSettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const userMenuRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    setMobileOpen(false);
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  const cartCount = getCartCount();

  return (
    <>
      <nav className="navbar">
        <div className="container navbar-content">
          <Link to="/" className="navbar-brand" onClick={() => setMobileOpen(false)}>
            <Logo variant="mark" size={36} />
            <div className="brand-text">
              <strong>Centre Techniques</strong>
              <small>Pianos</small>
            </div>
          </Link>

          <div className="navbar-links desktop-only">
            <NavLink to="/" end>Home</NavLink>
            <NavLink to="/products">Shop</NavLink>
            <NavLink to="/services">Services</NavLink>
            <NavLink to="/consultancy">Consultancy</NavLink>
            <NavLink to="/contact">Contact</NavLink>
            {user && <NavLink to="/my-orders">My Orders</NavLink>}
          </div>

          <form onSubmit={handleSearch} className="navbar-search desktop-only">
            <FaSearch className="search-icon" />
            <input
              type="search"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>

          <div className="navbar-actions">
            <button
              className="icon-btn mobile-only"
              onClick={() => setSearchOpen(!searchOpen)}
              aria-label="Search"
            >
              <FaSearch />
            </button>

            <Link to="/cart" className="icon-btn cart-icon" aria-label="Cart">
              <FaShoppingCart />
              {cartCount > 0 && <span className="cart-badge">{cartCount}</span>}
            </Link>

            {user ? (
              <div className="user-menu-wrapper" ref={userMenuRef}>
                <button
                  className="user-menu-trigger"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <span className="user-avatar">{user.name?.charAt(0).toUpperCase()}</span>
                  <span className="user-name desktop-only">{user.name?.split(' ')[0]}</span>
                </button>

                {userMenuOpen && (
                  <div className="user-dropdown fade-in">
                    <div className="user-dropdown-header">
                      <strong>{user.name}</strong>
                      <small>{user.email}</small>
                    </div>
                    <div className="user-dropdown-body">
                      {['admin', 'manager', 'editor'].includes(user.role) && (
                        <Link to="/admin" onClick={() => setUserMenuOpen(false)}>
                          <FaTachometerAlt /> Admin Dashboard
                        </Link>
                      )}
                      <Link to="/profile" onClick={() => setUserMenuOpen(false)}>
                        <FaUserCircle /> My Profile
                      </Link>
                      <Link to="/my-orders" onClick={() => setUserMenuOpen(false)}>
                        <FaBoxOpen /> My Orders
                      </Link>
                      <button onClick={handleLogout} className="logout-btn">
                        <FaSignOutAlt /> Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="auth-buttons desktop-only">
                <Link to="/login" className="btn btn-ghost btn-sm">Login</Link>
                <Link to="/register" className="btn btn-primary btn-sm">Sign up</Link>
              </div>
            )}

            <button
              className="icon-btn mobile-only"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Menu"
            >
              {mobileOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <form onSubmit={handleSearch} className="mobile-search mobile-only">
            <FaSearch />
            <input
              autoFocus
              type="search"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        )}
      </nav>

      {mobileOpen && (
        <div className="mobile-menu mobile-only">
          <NavLink to="/" end onClick={() => setMobileOpen(false)}>Home</NavLink>
          <NavLink to="/products" onClick={() => setMobileOpen(false)}>Shop</NavLink>
          <NavLink to="/services" onClick={() => setMobileOpen(false)}>Services</NavLink>
          <NavLink to="/consultancy" onClick={() => setMobileOpen(false)}>Consultancy</NavLink>
          <NavLink to="/contact" onClick={() => setMobileOpen(false)}>Contact</NavLink>
          {user ? (
            <>
              <NavLink to="/my-orders" onClick={() => setMobileOpen(false)}>My Orders</NavLink>
              <NavLink to="/profile" onClick={() => setMobileOpen(false)}>Profile</NavLink>
              {['admin', 'manager', 'editor'].includes(user.role) && (
                <NavLink to="/admin" onClick={() => setMobileOpen(false)}>Admin</NavLink>
              )}
              <button onClick={handleLogout} className="mobile-logout">
                <FaSignOutAlt /> Sign out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="btn btn-secondary btn-block">Login</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)} className="btn btn-primary btn-block">Sign up</Link>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default Navbar;
